/**
 * Roles List Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Grid of role cards with loading and empty states.
 */

import React from 'react';
import { Shield } from 'lucide-react';
import type { Role } from '../../../types/admin';
import RoleCard from './RoleCard';

interface RolesListProps {
  roles: Role[];
  isLoading?: boolean;
  onEdit: (role: Role) => void;
  onDuplicate: (role: Role) => void;
  onDelete: (role: Role) => void;
}

// Skeleton loader for role cards
const RoleCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
    <div className="flex items-start justify-between">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-gray-200" />
        <div>
          <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
          <div className="h-3 w-40 bg-gray-200 rounded" />
        </div>
      </div>
      <div className="w-6 h-6 bg-gray-200 rounded" />
    </div>
    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
      <div className="h-3 w-16 bg-gray-200 rounded" />
      <div className="h-3 w-20 bg-gray-200 rounded" />
    </div>
  </div>
);

const RolesList: React.FC<RolesListProps> = ({
  roles,
  isLoading = false,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  // Loading state
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <RoleCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  // Empty state
  if (roles.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <Shield size={48} className="mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-1">No roles found</h3>
        <p className="text-gray-500">
          Create a custom role to define specific permissions for your team.
        </p>
      </div>
    );
  }

  // Separate system roles from custom roles
  const systemRoles = roles.filter((r) => r.is_system_role);
  const customRoles = roles.filter((r) => !r.is_system_role);

  return (
    <div className="space-y-6">
      {/* System roles */}
      {systemRoles.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-3">System Roles</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {systemRoles.map((role) => (
              <RoleCard
                key={role.id}
                role={role}
                onEdit={onEdit}
                onDuplicate={onDuplicate}
                onDelete={onDelete}
              />
            ))}
          </div>
        </div>
      )}

      {/* Custom roles */}
      {customRoles.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-3">Custom Roles</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {customRoles.map((role) => (
              <RoleCard
                key={role.id}
                role={role}
                onEdit={onEdit}
                onDuplicate={onDuplicate}
                onDelete={onDelete}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesList;
